import Header from '@/components/Header';
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/App';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { ArrowLeft, History, Camera, TrendingUp, ExternalLink } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function HistoryPage() {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [analyses, setAnalyses] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchHistory();
  }, []);

  const fetchHistory = async () => {
    try {
      const response = await axios.get(`${API}/analysis/history`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAnalyses(response.data);
    } catch (error) {
      toast.error('Failed to load history');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getModeIcon = (mode) => {
    return mode === 'snapshot' ? <Camera className="w-5 h-5" /> : <TrendingUp className="w-5 h-5" />;
  };

  const getModeColor = (mode) => {
    return mode === 'snapshot' ? 'text-[#0891b2]' : 'text-blue-400';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F3F4F6] dark:bg-[#0b1220] transition-colors duration-200">
        <Header />
        <div className="flex items-center justify-center pt-20">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-500"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F3F4F6] dark:bg-[#0b1220] transition-colors duration-200" data-testid="history-page">
      <Header />
      <div className="max-w-6xl mx-auto p-6 space-y-6">
        <div className="flex items-center space-x-4">
          <Button
            onClick={() => navigate('/')}
            data-testid="back-button"
            variant="outline"
            className="border-[#0891b2] hover:bg-[#06748c] text-white bg-[#0891b2] hover:bg-[#06748c] text-white text-white"
          >
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div>
            <h1 className="text-3xl font-bold text-[#0891b2]">Analysis History</h1>
            <p className="text-gray-600">View all past FCR analyses</p>
          </div>
        </div>

        {analyses.length === 0 ? (
          <Card className="glass-card border-slate-200">
            <CardContent className="py-12 text-center">
              <History className="w-16 h-16 text-gray-600 mx-auto mb-4" />
              <p className="text-gray-600">No analyses yet. Create your first one!</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {analyses.map((analysis) => (
              <Card
                key={analysis.id}
                className="glass-card border-slate-200 cursor-pointer hover:bg-[#06748c] text-white border-[#0891b2] transition-all"
                onClick={() => navigate(`/analysis/${analysis.id}`)}
                data-testid={`analysis-card-${analysis.id}`}
              >
                <CardHeader>
                  <div className="flex justify-between items-start">
                    <div className="flex items-center space-x-3">
                      <div className={`${getModeColor(analysis.mode)}`}>
                        {getModeIcon(analysis.mode)}
                      </div>
                      <div>
                        <CardTitle className={getModeColor(analysis.mode)}>
                          {analysis.mode === 'snapshot' ? 'Snapshot Analysis' : 'Daily Tracking'}
                        </CardTitle>
                        <CardDescription className="text-gray-500">
                          {formatDate(analysis.created_at)}
                        </CardDescription>
                      </div>
                    </div>
                    <ExternalLink className="w-4 h-4 text-gray-500" />
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid md:grid-cols-3 gap-4">
                    <div>
                      <p className="text-sm text-gray-600">Species</p>
                      <p className="text-gray-200" data-testid={`species-${analysis.id}`}>{analysis.species}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Location</p>
                      <p className="text-gray-200" data-testid={`location-${analysis.id}`}>{analysis.farm_location}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Genetic Origin</p>
                      <p className="text-gray-200" data-testid={`origin-${analysis.id}`}>{analysis.genetic_origin}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}